import { ChangeDetectorRef, Component, forwardRef, Input, OnInit, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { UtilsService } from '../../UtilsService';
import customeOptions from 'src/assets/json/customeOptions.json';
import { HttpClient } from '@angular/common/http';
import { Claimant, ClaimantRepBy, Respondent, RespondentRepBy } from 'src/app/_models/party';
import { ActivatedRoute } from '@angular/router';

const ONDA_AL ='onda-al';

// ===== CONSTANTS =====
const PARTY_TYPE_VALUES = {
  OTHER: 5,
  COMPANY: 2
} as const;

const PARTY_TYPES = {
  CLAIMANT: 'claimant',
  CLAIMANT_REP: 'claimantRepBy',
  RESPONDENT: 'respondent',
  RESPONDENT_REP: 'respondentRepBy'
} as const;

const PARTY_CATEGORIES = {
  CLAIMANT: 'Claimant',
  RESPONDENT: 'Respondent'
} as const;

const EMAIL_PATTERN = /^(([^<>()[\]\.,;:\s@"]+(\.[^<>()[\]\.,;:\s@"]+)*)|(".+"))@(([^<>()[\]\.,;:\s@"]+\.)+[^<>()[\]\.,;:\s@"]{2,})$/i;

// REPLACE the existing FIELD_NAMES with:
const FIELD_NAMES = {
  MANAGER_CLAIMANT: 'managerClaimant',
  MANAGER_RESPONDENT: 'managerRespondent',
  TYPE_CLAIMANT_OTHER: 'typeClaimantOther',
  TYPE_RESPONDENT_OTHER: 'typeRespondentOther',
  TYPE_OF_PARTY_OTHER: 'typeOfPartyOther'  // Add this line
} as const;

const PARTY_TYPE_LABELS = {
  [PARTY_TYPES.CLAIMANT]: 'Requesting Party',
  [PARTY_TYPES.CLAIMANT_REP]: 'Requesting Party Representative',
  [PARTY_TYPES.RESPONDENT]: 'Responding Party',
  [PARTY_TYPES.RESPONDENT_REP]: 'Responding Party Representative'
} as const;

// ===== INTERFACES =====
interface MainPartyClaimant extends Claimant {
  typeClaimant?: string;
  managerComplainant?: string;
  typeClaimantOther?: string;
  genderClaimant?: string;
}

interface MainPartyRespondent extends Respondent {
  typeRespondent?: string;
  managerRespondent?: string;
  typeRespondentOther?: string;
  genderRespondent?: string;
}

interface AdditionalParty {
  id?: number;
  partyType?: PartyType;
  
  // Common fields with generic names
  typeOfParty?: string;
  typeOfPartyOther?: string;
  gender?: string;
  
  // Field mappings for claimant
  nameClaimant?: string;
  addressClaimant?: string;
  stateOrCountyClaimant?: string;
  countryClaimantCode?: string;
  countryClaimant?: string;
  telephoneClaimant?: string;
  emailClaimant?: string;
  
  // Field mappings for respondent
  nameRespondent?: string;
  addressRespondent?: string;
  stateOrCountyRespondent?: string;
  countryRespondantCode?: string;
  countryRespondent?: string;
  telephoneRespondent?: string;
  emailRespondent?: string;
}

interface EditableParty {
  name?: string;
  address?: string;
  stateOrCounty?: string;
  countryCode?: string;
  telephone?: string;
  email?: string;

  // Main party fields
  prefix?: string;
  typeClaimant?: string;
  typeRespondent?: string;
  managerComplainant?: string;
  managerRespondent?: string;
  typeClaimantOther?: string;
  typeRespondentOther?: string;
  genderClaimant?: string;
  genderRespondent?: string;

  // Additional party fields
  typeOfParty?: string;
  typeOfPartyOther?: string;
  gender?: string;
}

interface AllParties {
  claimants: (Claimant | AdditionalParty)[];
  claimantRepBys: ClaimantRepBy[];
  respondents: (Respondent | AdditionalParty)[];
  respondentRepBys: RespondentRepBy[];
}

type PartyType = typeof PARTY_TYPES[keyof typeof PARTY_TYPES];
type PartyCategory = typeof PARTY_CATEGORIES[keyof typeof PARTY_CATEGORIES];

interface ValidationState {
  validName: boolean;
  validAddress: boolean;
  validStateOrCounty: boolean;
  validPrefix: boolean;
  validTel: boolean;
  validEmail: boolean;
  ValidationName: boolean;
  invalidEmailsValidation: boolean;
  validCharActiveFlagValidation: boolean;
  validTypeClaimantOther: boolean;
  validTypeRespondentOther: boolean;
  validTypeOfPartyOther: boolean;
  validmanagerComplainant: boolean;
  validmanagerRespondent: boolean;
}

interface FieldVisibility {
  showTypeOther: boolean;
  showManager: boolean;
}

@Component({
  selector: 'app-party-information',
  standalone: false,
  templateUrl: './party-information.component.html',
  styleUrls: ['./party-information.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => PartyInformationComponent),
    multi: true
  }]
})
export class PartyInformationComponent implements OnInit, OnDestroy, ControlValueAccessor {
  // ===== INPUTS =====
  @Input() editCase = true;
  @Input() dispCountry = true;
  @Input() readOnly = false;
  @Input() office = '';
  @Input() officeDetails: any = {};

  blocked = false;

  // ===== OPTIONS =====
  genderOptions: any[] = [];
  typeOfPartyOptions: any[] = [];
  optionsCountries: any[] = [];

  // ===== PARTY DATA =====
  claimants: (MainPartyClaimant | AdditionalParty)[] = [];
  claimantRepBys: ClaimantRepBy[] = [];
  respondents: (MainPartyRespondent | AdditionalParty)[] = [];
  respondentRepBys: RespondentRepBy[] = [];

  // ===== EDITING STATE =====
  editAdd = false;
  editedPartyData: EditableParty = {};
  currentPartyType: PartyType = PARTY_TYPES.CLAIMANT;
  currentEditingId: number | null = null;
  isEditMode = false;
  newPartyData: EditableParty = {};

  // ===== TRACKING =====
  private currentEditingPartyType: PartyType | null = null;
  private currentAddingPartyType: PartyType | null = null;
  private currentEditingPartyIndex: { type: PartyType; index: number } | null = null;
  private partyBackup: any = null;
  private partyBackupAtIndex: any = null;
  private fieldVisibility = new Map<string, FieldVisibility>();
  private accordionStates = new Map<string, boolean>();

  // ===== VALIDATION STATE =====
  invalidEmailsValidation = false;
  validCharActiveFlagValidation = false;
  validName = false;
  validAddress = false;
  validStateOrCounty = false;
  validPrefix = false;
  validTel = false;
  validEmail = false;
  ValidationName = false;
  validChar: boolean[] = [];

  // ===== FIELD VISIBILITY =====
  managerClaimantField = false;
  managerRespondentField = false;
  displaytypeClaimantOtherField = false;
  displaytypeRespondentOtherField = false;
  displayTypeOfPartyOtherField = false;

  // ===== DYNAMIC FIELD VALIDATION =====
  validmanagerComplainant = true;
  validmanagerRespondent = true;
  validTypeClaimantOther = true;
  validTypeRespondentOther = true;
  validTypeOfPartyOther = true;
  managerComplainantLengthError = false;
  managerRespondentLengthError = false;

  // ===== LANGUAGE =====
  currentLanguage = 'en';
  private translateSubscription: Subscription;

  // ===== EXPOSE CONSTANTS TO TEMPLATE =====
  readonly PARTY_TYPE_VALUES = PARTY_TYPE_VALUES;
  readonly PARTY_TYPES = PARTY_TYPES;

  office_ONDA_AL = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private utilsService: UtilsService,
    private translate: TranslateService,
    private http: HttpClient ,private activatedRoute: ActivatedRoute
  ) { }

  // ===== LIFECYCLE HOOKS =====
  ngOnInit(): void {
    try {
      this.blocked = true;
      this.activatedRoute.params.subscribe(async params => {
        const { lang, officeName, id } = params;
        this.office_ONDA_AL =(officeName === ONDA_AL) ? true : false;
        this.office = officeName;
        this.currentLanguage = lang;
        this.loadData();
      });
    } catch (error) {
      console.error('Error during component initialization:', error);
      this.blocked = false;
    }
  }

  loadData(){
     this.translateSubscription = this.translate.onLangChange.subscribe(() => {
      this.currentLanguage = this.translate.currentLang;
      this.loadOptions();
      this.updateAllCountries();
    });
    this.loadOptions();
    this.blocked = false;
  }

  ngOnDestroy(): void {
    this.translateSubscription?.unsubscribe();
  }

  // ===== OPTIONS LOADING =====
  private loadOptions(): void {
    this.optionsCountries = this.utilsService.loadCountryOption();
    if(this.editCase) {
      this.typeOfPartyOptions = (this.office_ONDA_AL) ? this.utilsService.get_ONDA_typeOfPartyOptions() : this.utilsService.getTypeOfPartyOptions(); 
      this.genderOptions = this.utilsService.loadOptions(customeOptions.genderOptions);
    }
  }

  // ===== CONTROL VALUE ACCESSOR =====
  onChange: any = () => { };
  onTouch: any = () => { };

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  writeValue(allParties: AllParties): void {
    if (this.utilsService.isBlank(allParties)) {
      this.initializeEmptyParties();
      return;
    }

    this.claimants = this.ensureArray(allParties.claimants);
    this.claimantRepBys = this.ensureArray(allParties.claimantRepBys);
    this.respondents = this.ensureArray(allParties.respondents);
    this.respondentRepBys = this.ensureArray(allParties.respondentRepBys);

    this.addPartyTypeTracking();
    this.loadOptionValue();
    this.updateAllCountries();
  }

  // ===== HELPER METHODS =====
  private initializeEmptyParties(): void {
    this.claimants = [];
    this.claimantRepBys = [];
    this.respondents = [];
    this.respondentRepBys = [];
  }

  private ensureArray<T>(data: T | T[]): T[] {
    if (Array.isArray(data)) return data;
    return data ? [data] : [];
  }

  private addPartyTypeTracking(): void {
    this.claimants.forEach(p => (p as any).partyType = PARTY_TYPES.CLAIMANT);
    this.claimantRepBys.forEach(p => (p as any).partyType = PARTY_TYPES.CLAIMANT_REP);
    this.respondents.forEach(p => (p as any).partyType = PARTY_TYPES.RESPONDENT);
    this.respondentRepBys.forEach(p => (p as any).partyType = PARTY_TYPES.RESPONDENT_REP);
  }

  private emitChanges(): void {
    const allParties: AllParties = {
      claimants: this.claimants,
      claimantRepBys: this.claimantRepBys,
      respondents: this.respondents,
      respondentRepBys: this.respondentRepBys
    };
    this.onChange(allParties);
  }

  // ===== FIELD KEY MANAGEMENT =====
  private getFieldKey(partyType: PartyType, index: number | 'new'): string {
    return `${partyType}_${index}`;
  }

  private getAccordionKey(partyType: PartyType, index: number): string {
    return `${partyType}_${index}`;
  }

  // ===== CHECK IF MAIN OR ADDITIONAL PARTY =====
  isMainParty(partyType: PartyType, index: number): boolean {
    return index === 0 && (partyType === PARTY_TYPES.CLAIMANT || partyType === PARTY_TYPES.RESPONDENT);
  }

  isAdditionalParty(partyType: PartyType, index: number): boolean {
    return index > 0 && (partyType === PARTY_TYPES.CLAIMANT || partyType === PARTY_TYPES.RESPONDENT);
  }

  isRepresentative(partyType: PartyType): boolean {
    return partyType === PARTY_TYPES.CLAIMANT_REP || partyType === PARTY_TYPES.RESPONDENT_REP;
  }

  // ===== FIELD VISIBILITY FOR MAIN PARTIES =====
  shouldShowTypeOtherMain(partyType: PartyType, index: number): boolean {
    if (!this.isMainParty(partyType, index)) return false;

    const party = this.getPartyByTypeAndIndex(partyType, index);
    if (partyType === PARTY_TYPES.CLAIMANT) {
      return Number(party?.typeClaimant) === PARTY_TYPE_VALUES.OTHER;
    } else if (partyType === PARTY_TYPES.RESPONDENT) {
      return Number(party?.typeRespondent) === PARTY_TYPE_VALUES.OTHER;
    }
    return false;
  }

  shouldShowManagerMain(partyType: PartyType, index: number): boolean {
    // Manager field is only available for office_ONDA_AL
    if (!this.office_ONDA_AL) return false;
    if (!this.isMainParty(partyType, index)) return false;

    const party = this.getPartyByTypeAndIndex(partyType, index);
    if (partyType === PARTY_TYPES.CLAIMANT) {
      return Number(party?.typeClaimant) === PARTY_TYPE_VALUES.COMPANY;
    } else if (partyType === PARTY_TYPES.RESPONDENT) {
      return Number(party?.typeRespondent) === PARTY_TYPE_VALUES.COMPANY;
    }
    return false;
  }

  // ===== FIELD VISIBILITY FOR ADDITIONAL PARTIES =====
  shouldShowTypeOtherAdditional(partyType: PartyType, index: number): boolean {
    if (!this.isAdditionalParty(partyType, index)) return false;

    const party = this.getPartyByTypeAndIndex(partyType, index) as AdditionalParty;
    return Number(party?.typeOfParty) === PARTY_TYPE_VALUES.OTHER;
  }


  // ===== PARTY TYPE SELECTION FOR MAIN PARTIES =====
  selectPartyTypeMain(typeValue: any, partyCategory: PartyCategory, index: number): void {
    const selectedType = Number(typeValue);
    const isOtherType = selectedType === PARTY_TYPE_VALUES.OTHER;
    const isCompanyType = selectedType === PARTY_TYPE_VALUES.COMPANY;

    const partyType = partyCategory === PARTY_CATEGORIES.CLAIMANT ? PARTY_TYPES.CLAIMANT : PARTY_TYPES.RESPONDENT;
    const party = this.getPartyByTypeAndIndex(partyType, index);

    if (partyCategory === PARTY_CATEGORIES.CLAIMANT) {
      if (!isOtherType) {
        party.typeClaimantOther = '';
        this.validTypeClaimantOther = true;
      }
      if (!isCompanyType) {
        party.managerComplainant = '';
        this.validmanagerComplainant = true;
      }
    } else {
      if (!isOtherType) {
        party.typeRespondentOther = '';
        this.validTypeRespondentOther = true;
      }
      if (!isCompanyType) {
        party.managerRespondent = '';
        this.validmanagerRespondent = true;
      }
    }

    this.cdr.detectChanges();
  }

  // ===== PARTY TYPE SELECTION FOR ADDITIONAL PARTIES =====
  selectPartyTypeAdditional(typeValue: any, partyType: PartyType, index: number): void {
    const selectedType = Number(typeValue);
    const isOtherType = selectedType === PARTY_TYPE_VALUES.OTHER;

    const party = this.getPartyByTypeAndIndex(partyType, index) as AdditionalParty;

    if (!isOtherType) {
      party.typeOfPartyOther = '';
      this.validTypeOfPartyOther = true;
    }

    this.cdr.detectChanges();
  }

  // ===== PARTY TYPE SELECTION FOR NEW MAIN PARTY =====
  selectPartyTypeForNewMain(typeValue: any, partyCategory: PartyCategory): void {
    const selectedType = Number(typeValue);
    const isOtherType = selectedType === PARTY_TYPE_VALUES.OTHER;
    // Manager field is only available for office_ONDA_AL
    const isCompanyType = this.office_ONDA_AL && selectedType === PARTY_TYPE_VALUES.COMPANY;

    if (partyCategory === PARTY_CATEGORIES.CLAIMANT) {
      this.displaytypeClaimantOtherField = isOtherType;
      this.managerClaimantField = isCompanyType;
      this.validTypeClaimantOther = true;
      this.validmanagerComplainant = true;

      if (!isOtherType) this.newPartyData.typeClaimantOther = '';
      if (!isCompanyType) this.newPartyData.managerComplainant = '';
    } else {
      this.displaytypeRespondentOtherField = isOtherType;
      this.managerRespondentField = isCompanyType;
      this.validTypeRespondentOther = true;
      this.validmanagerRespondent = true;

      if (!isOtherType) this.newPartyData.typeRespondentOther = '';
      if (!isCompanyType) this.newPartyData.managerRespondent = '';
    }

    this.cdr.detectChanges();
  }

  // ===== PARTY TYPE SELECTION FOR NEW ADDITIONAL PARTY =====
  selectPartyTypeForNewAdditional(typeValue: any): void {
    const selectedType = Number(typeValue);
    const isOtherType = selectedType === PARTY_TYPE_VALUES.OTHER;

    this.displayTypeOfPartyOtherField = isOtherType;
    this.validTypeOfPartyOther = true;

    if (!isOtherType) {
      this.newPartyData.typeOfPartyOther = '';
    }

    this.cdr.detectChanges();
  }

  // ===== INPUT VALIDATION =====
  insertData(event: any, field: string): void {
    const inputElement = event.target as HTMLInputElement;
    const value = inputElement.value?.trim();
    const fullValue = inputElement.value || '';

    // Name validation - check both empty and character count
    if (field.toLowerCase().includes('name')) {
      this.ValidationName = !value;
      this.validName = this.utilsService.countChars(fullValue);
      return;
    }

    // Address character count validation
    if (field.toLowerCase().includes('address')) {
      this.validAddress = this.utilsService.countChars(fullValue);
      return;
    }

    // StateOrCounty character count validation
    if (field.toLowerCase().includes('stateorcounty')) {
      this.validStateOrCounty = this.utilsService.countChars(fullValue);
      return;
    }

    // Prefix character count validation
    if (field.toLowerCase().includes('prefix')) {
      this.validPrefix = this.utilsService.countChars(fullValue);
      return;
    }

    // Telephone character count validation
    if (field.toLowerCase().includes('telephone')) {
      this.validTel = this.utilsService.countChars(fullValue);
      return;
    }

    // Email character count validation
    if (field.toLowerCase().includes('email')) {
      this.validEmail = this.utilsService.countChars(fullValue);
      return;
    }

    // Dynamic field validation
    switch (field) {
      case FIELD_NAMES.MANAGER_CLAIMANT:
        this.validmanagerComplainant = !!value;
        this.managerComplainantLengthError = this.utilsService.countChars(fullValue);
        break;
      case FIELD_NAMES.MANAGER_RESPONDENT:
        this.validmanagerRespondent = !!value;
        this.managerRespondentLengthError = this.utilsService.countChars(fullValue);
        break;
      case FIELD_NAMES.TYPE_CLAIMANT_OTHER:
        this.validTypeClaimantOther = !!value;
        break;
      case FIELD_NAMES.TYPE_RESPONDENT_OTHER:
        this.validTypeRespondentOther = !!value;
        break;
      case FIELD_NAMES.TYPE_OF_PARTY_OTHER:
        this.validTypeOfPartyOther = !!value;
        break;
    }
  }

  // ===== EDIT MODE MANAGEMENT =====
  isEditingPartyAtIndex(partyType: PartyType, index: number): boolean {
    return this.currentEditingPartyIndex !== null &&
      this.currentEditingPartyIndex.type === partyType &&
      this.currentEditingPartyIndex.index === index;
  }

  toggleEditPartyAtIndex(partyType: PartyType, index: number, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    const key = this.getAccordionKey(partyType, index);
    const isCurrentlyEditing = this.isEditingPartyAtIndex(partyType, index);
    const isAccordionOpen = this.accordionStates.get(key);

    this.onAccordionToggle(partyType, index, isAccordionOpen);

    if (isCurrentlyEditing) {
      this.exitEditMode(partyType, index);
    } else {
      this.enterEditMode(partyType, index);
    }

    this.cdr.detectChanges();
  }

  private exitEditMode(partyType: PartyType, index: number): void {
    this.currentEditingPartyIndex = null;
    this.partyBackupAtIndex = null;
    const key = this.getAccordionKey(partyType, index);
    this.accordionStates.set(key, false);
  }

  private enterEditMode(partyType: PartyType, index: number): void {
    const key = this.getAccordionKey(partyType, index);
    this.accordionStates.set(key, true);

    setTimeout(() => {
      this.currentEditingPartyIndex = { type: partyType, index };
      this.createPartyBackupAtIndex(partyType, index);
      this.resetValidationFlags();
      this.restoreFieldVisibility(partyType, index);
      this.cdr.detectChanges();
    }, 50);
  }

  private resetValidationFlags(): void {
    this.validName = false;
    this.validAddress = false;
    this.validStateOrCounty = false;
    this.validPrefix = false;
    this.ValidationName = false;
    this.validTypeClaimantOther = true;
    this.validTypeRespondentOther = true;
    this.validTypeOfPartyOther = true;
    this.validmanagerComplainant = true;
    this.validmanagerRespondent = true;
    this.managerComplainantLengthError = false;
    this.managerRespondentLengthError = false;
  }

  // REPLACE the existing restoreFieldVisibility method with:
  private restoreFieldVisibility(partyType: PartyType, index: number): void {
    if (this.isMainParty(partyType, index)) {
      if (partyType === PARTY_TYPES.CLAIMANT) {
        const party = this.claimants[index] as MainPartyClaimant;
        const typeValue = Number(party.typeClaimant);
        this.selectPartyTypeMain(typeValue, PARTY_CATEGORIES.CLAIMANT, index);
      } else if (partyType === PARTY_TYPES.RESPONDENT) {
        const party = this.respondents[index] as MainPartyRespondent;
        const typeValue = Number(party.typeRespondent);
        this.selectPartyTypeMain(typeValue, PARTY_CATEGORIES.RESPONDENT, index);
      }
    } else if (this.isAdditionalParty(partyType, index)) {
      const party = this.getPartyByTypeAndIndex(partyType, index) as AdditionalParty;
      const typeValue = Number(party.typeOfParty);
      this.selectPartyTypeAdditional(typeValue, partyType, index);
    }
  }

  private createPartyBackupAtIndex(partyType: PartyType, index: number): void {
    const parties = this.getPartiesArray(partyType);
    this.partyBackupAtIndex = JSON.parse(JSON.stringify(parties[index]));
  }

  cancelEditPartyAtIndex(partyType: PartyType, index: number, skipAccordionClose = false): void {
    if (this.partyBackupAtIndex) {
      const parties = this.getPartiesArray(partyType);
      parties[index] = { ...this.partyBackupAtIndex };
    }

    this.currentEditingPartyIndex = null;
    this.partyBackupAtIndex = null;
    this.resetValidationFlags();

    if (!skipAccordionClose) {
      const key = this.getAccordionKey(partyType, index);
      this.accordionStates.set(key, false);
    }

    this.cdr.detectChanges();
  }

  async saveEditedPartyAtIndex(partyType: PartyType, index: number): Promise<void> {
    const party = this.getPartyByTypeAndIndex(partyType, index);

    if (!this.validatePartyForSaveAtIndex(partyType, index, party)) {
      this.cdr.detectChanges();
      return;
    }

    try {
      await this.updateAllCountries();
      this.emitChanges();

      this.currentEditingPartyIndex = null;
      this.partyBackupAtIndex = null;
      this.resetValidationFlags();

      const key = this.getAccordionKey(partyType, index);
      this.accordionStates.set(key, false);

      this.cdr.detectChanges();
      this.saveAllParties();
    } catch (error) {
      console.error('Error saving party:', error);
    }
  }

  // ADD these methods for edit validation:

  private validatePartyForSaveAtIndex(partyType: PartyType, index: number, party: any): boolean {
    let hasError = false;
    this.validCharActiveFlagValidation = false;
    
    // Reset individual validation flags
    this.validName = false;
    this.validAddress = false;
    this.validStateOrCounty = false;
    this.validPrefix = false;
    this.validTel = false;
    this.validEmail = false;

    // Validate name
    const nameField = this.getNameFieldForPartyType(partyType);
    if (!party[nameField]?.trim()) {
      this.ValidationName = true;
      hasError = true;
    } else {
      this.ValidationName = false;
    }

    // Validate dynamic fields based on party type and index
    if (this.isMainParty(partyType, index)) {
      if (partyType === PARTY_TYPES.CLAIMANT) {
        hasError = this.validateMainClaimantFields(party) || hasError;
      } else if (partyType === PARTY_TYPES.RESPONDENT) {
        hasError = this.validateMainRespondentFields(party) || hasError;
      }
    } else if (this.isAdditionalParty(partyType, index)) {
      hasError = this.validateAdditionalPartyFieldsForSave(party) || hasError;
    }

    // Validate character counts (255 char limit)
    for (const key in party) {
      if (party[key] && typeof party[key] === 'string') {
        const exceedsLimit = this.utilsService.countChars(party[key]);
        this.validChar[key] = exceedsLimit;
        if (exceedsLimit) {
          this.validCharActiveFlagValidation = true;
          hasError = true;
          // Set individual flags for HTML error display
          if (key.toLowerCase().includes('name')) {
            this.validName = true;
          }
        }
      }
    }

    // Check email validation
    if (this.invalidEmailsValidation) {
      hasError = true;
    }

    return !hasError;
  }

  private getNameFieldForPartyType(partyType: PartyType): string {
    switch (partyType) {
      case PARTY_TYPES.CLAIMANT: return 'nameClaimant';
      case PARTY_TYPES.CLAIMANT_REP: return 'nameClaimantRepBy';
      case PARTY_TYPES.RESPONDENT: return 'nameRespondent';
      case PARTY_TYPES.RESPONDENT_REP: return 'nameRespondentRepBy';
      default: return 'name';
    }
  }

  private validateMainClaimantFields(party: MainPartyClaimant): boolean {
    let hasError = false;
    const typeValue = Number(party.typeClaimant);

    if (typeValue === PARTY_TYPE_VALUES.OTHER && !party.typeClaimantOther?.trim()) {
      this.validTypeClaimantOther = false;
      hasError = true;
    } else {
      this.validTypeClaimantOther = true;
    }

    // Manager field is only required for onda_al office
    if (this.office_ONDA_AL && typeValue === PARTY_TYPE_VALUES.COMPANY && !party.managerComplainant?.trim()) {
      this.validmanagerComplainant = false;
      hasError = true;
    } else {
      this.validmanagerComplainant = true;
    }

    return hasError;
  }

  private validateMainRespondentFields(party: MainPartyRespondent): boolean {
    let hasError = false;
    const typeValue = Number(party.typeRespondent);

    if (typeValue === PARTY_TYPE_VALUES.OTHER && !party.typeRespondentOther?.trim()) {
      this.validTypeRespondentOther = false;
      hasError = true;
    } else {
      this.validTypeRespondentOther = true;
    }

    // Manager field is only required for onda_al office
    if (this.office_ONDA_AL && typeValue === PARTY_TYPE_VALUES.COMPANY && !party.managerRespondent?.trim()) {
      this.validmanagerRespondent = false;
      hasError = true;
    } else {
      this.validmanagerRespondent = true;
    }

    return hasError;
  }

  private validateAdditionalPartyFieldsForSave(party: AdditionalParty): boolean {
    let hasError = false;
    const typeValue = Number(party.typeOfParty);

    if (typeValue === PARTY_TYPE_VALUES.OTHER && !party.typeOfPartyOther?.trim()) {
      this.validTypeOfPartyOther = false;
      hasError = true;
    } else {
      this.validTypeOfPartyOther = true;
    }

    return hasError;
  }


  // ===== ADD PARTY METHODS =====
  isAddingParty(partyType: PartyType): boolean {
    return this.currentAddingPartyType === partyType;
  }

  startAddingParty(partyType: PartyType): void {
    this.currentAddingPartyType = partyType;
    this.newPartyData = {};
    this.resetValidation();
  }

  cancelAddingParty(partyType: PartyType): void {
    this.currentAddingPartyType = null;
    this.newPartyData = {};
    this.resetValidation();
  }

 async saveNewParty(partyType: PartyType): Promise<void> {
  this.ValidationName = false;
  this.validCharActiveFlagValidation = false;

  const isMainParty = (partyType === PARTY_TYPES.CLAIMANT && this.claimants.length === 0) ||
                      (partyType === PARTY_TYPES.RESPONDENT && this.respondents.length === 0);

  if (!this.validateNewPartyByType(partyType, isMainParty)) {
    this.cdr.detectChanges();
    return;
  }

  const nextId = this.getNextIdForPartyType(partyType);
  let specificParty;

  // DEBUG: Log what we're about to save
  console.log('🔍 Saving New Party Debug:');
  console.log('Party Type:', partyType);
  console.log('Is Main Party:', isMainParty);
  console.log('New Party Data:', JSON.stringify(this.newPartyData, null, 2));

  if (isMainParty) {
    specificParty = this.mapFromEditablePartyMain(this.newPartyData, partyType, nextId);
  } else if (this.isRepresentative(partyType)) {
    specificParty = this.mapFromEditablePartyRepresentative(this.newPartyData, partyType, nextId);
  } else {
    specificParty = this.mapFromEditablePartyAdditional(this.newPartyData, partyType, nextId);
  }

  // DEBUG: Log the mapped party
  console.log('📦 Mapped Party Object:', JSON.stringify(specificParty, null, 2));

  this.getPartiesArray(partyType).push(specificParty);

  await this.updateAllCountries();
  this.emitChanges();

  this.currentAddingPartyType = null;
  this.newPartyData = {};
  this.resetValidation();

  this.cdr.detectChanges();
  this.saveAllParties();
}

  // ADD these validation methods:

  private validateNewPartyByType(partyType: PartyType, isMainParty: boolean): boolean {
    let hasError = false;
    
    // Reset validation flags
    this.validName = false;
    this.validAddress = false;
    this.validTel = false;
    this.validEmail = false;
    this.validCharActiveFlagValidation = false;

    // Validate name
    if (!this.newPartyData?.name?.trim()) {
      this.ValidationName = true;
      hasError = true;
    }

    if (isMainParty) {
      hasError = this.validateMainPartyFieldsForNew(partyType) || hasError;
    } else if (this.isRepresentative(partyType)) {
      // Representatives don't need type/gender validation
    } else {
      hasError = this.validateAdditionalPartyFieldsForNew() || hasError;
    }

    // Validate character counts
    for (const key in this.newPartyData) {
      if (this.newPartyData[key] && typeof this.newPartyData[key] === 'string') {
        const exceedsLimit = this.utilsService.countChars(this.newPartyData[key]);
        this.validChar[key] = exceedsLimit;
        
        if (exceedsLimit) {
          this.validCharActiveFlagValidation = true;
          hasError = true;
          
          // Set individual flags for HTML error display
          if (key.toLowerCase().includes('name')) {
            this.validName = true;
          }
          if (key.toLowerCase().includes('address')) {
            this.validAddress = true;
          }
          if (key.toLowerCase().includes('telephone')) {
            this.validTel = true;
          }
          if (key.toLowerCase().includes('email')) {
            this.validEmail = true;
          }
        }
      }
    }

    if (this.invalidEmailsValidation) {
      hasError = true;
    }

    return !hasError;
  }

  private validateMainPartyFieldsForNew(partyType: PartyType): boolean {
    let hasError = false;

    if (partyType === PARTY_TYPES.CLAIMANT) {
      const typeValue = Number(this.newPartyData.typeClaimant);

      if (typeValue === PARTY_TYPE_VALUES.OTHER && !this.newPartyData?.typeClaimantOther?.trim()) {
        this.validTypeClaimantOther = false;
        hasError = true;
      } else {
        this.validTypeClaimantOther = true;
      }

      if (typeValue === PARTY_TYPE_VALUES.COMPANY && !this.newPartyData?.managerComplainant?.trim()) {
        this.validmanagerComplainant = false;
        hasError = true;
      } else {
        this.validmanagerComplainant = true;
      }
    } else if (partyType === PARTY_TYPES.RESPONDENT) {
      const typeValue = Number(this.newPartyData.typeRespondent);

      if (typeValue === PARTY_TYPE_VALUES.OTHER && !this.newPartyData?.typeRespondentOther?.trim()) {
        this.validTypeRespondentOther = false;
        hasError = true;
      } else {
        this.validTypeRespondentOther = true;
      }

      if (typeValue === PARTY_TYPE_VALUES.COMPANY && !this.newPartyData?.managerRespondent?.trim()) {
        this.validmanagerRespondent = false;
        hasError = true;
      } else {
        this.validmanagerRespondent = true;
      }
    }

    return hasError;
  }

  private validateAdditionalPartyFieldsForNew(): boolean {
    let hasError = false;
    const typeValue = Number(this.newPartyData.typeOfParty);

    if (typeValue === PARTY_TYPE_VALUES.OTHER && !this.newPartyData?.typeOfPartyOther?.trim()) {
      this.validTypeOfPartyOther = false;
      hasError = true;
    } else {
      this.validTypeOfPartyOther = true;
    }

    return hasError;
  }


  // REPLACE the existing loadOptionValue method with:
  loadOptionValue(): void {
    this.claimants.forEach((claimant, index) => {
      if (this.isMainParty(PARTY_TYPES.CLAIMANT, index)) {
        const mainClaimant = claimant as MainPartyClaimant;
        const typeValue = Number(mainClaimant.typeClaimant);
        this.selectPartyTypeMain(typeValue, PARTY_CATEGORIES.CLAIMANT, index);
      } else {
        const additionalClaimant = claimant as AdditionalParty;
        const typeValue = Number(additionalClaimant.typeOfParty);
        this.selectPartyTypeAdditional(typeValue, PARTY_TYPES.CLAIMANT, index);
      }
    });

    this.respondents.forEach((respondent, index) => {
      if (this.isMainParty(PARTY_TYPES.RESPONDENT, index)) {
        const mainRespondent = respondent as MainPartyRespondent;
        const typeValue = Number(mainRespondent.typeRespondent);
        this.selectPartyTypeMain(typeValue, PARTY_CATEGORIES.RESPONDENT, index);
      } else {
        const additionalRespondent = respondent as AdditionalParty;
        const typeValue = Number(additionalRespondent.typeOfParty);
        this.selectPartyTypeAdditional(typeValue, PARTY_TYPES.RESPONDENT, index);
      }
    });
  }

  // ===== ACCORDION MANAGEMENT =====
  isAccordionOpen(partyType: PartyType, index: number): boolean {
    const key = this.getAccordionKey(partyType, index);
    return this.accordionStates.get(key) || false;
  }

  onAccordionToggle(partyType: PartyType, index: number, isOpen: boolean): void {
    const key = this.getAccordionKey(partyType, index);
    this.accordionStates.set(key, isOpen);

    if (!isOpen && this.isEditingPartyAtIndex(partyType, index)) {
      this.cancelEditPartyAtIndex(partyType, index, true);
    }
  }

  // ===== HELPER METHODS =====
  private getPartiesArray(partyType: PartyType): any[] {
    switch (partyType) {
      case PARTY_TYPES.CLAIMANT: return this.claimants;
      case PARTY_TYPES.CLAIMANT_REP: return this.claimantRepBys;
      case PARTY_TYPES.RESPONDENT: return this.respondents;
      case PARTY_TYPES.RESPONDENT_REP: return this.respondentRepBys;
      default: return [];
    }
  }

  private getNextIdForPartyType(partyType: PartyType): number {
    const parties = this.getPartiesArray(partyType);
    if (parties.length === 0) return 1;
    return Math.max(...parties.map(p => p.id || 0)) + 1;
  }

  private getPartyByTypeAndIndex(partyType: PartyType, index: number): any {
    switch (partyType) {
      case PARTY_TYPES.CLAIMANT: return this.claimants[index];
      case PARTY_TYPES.RESPONDENT: return this.respondents[index];
      case PARTY_TYPES.CLAIMANT_REP: return this.claimantRepBys[index];
      case PARTY_TYPES.RESPONDENT_REP: return this.respondentRepBys[index];
    }
  }

  private getPartyTypeFromObject(party: any): PartyType {
    if (party.nameClaimant !== undefined) return PARTY_TYPES.CLAIMANT;
    if (party.nameClaimantRepBy !== undefined) return PARTY_TYPES.CLAIMANT_REP;
    if (party.nameRespondent !== undefined) return PARTY_TYPES.RESPONDENT;
    if (party.nameRespondentRepBy !== undefined) return PARTY_TYPES.RESPONDENT_REP;
    return PARTY_TYPES.CLAIMANT;
  }

  // ===== MAPPING METHODS FOR MAIN PARTIES =====
  private mapFromEditablePartyMain(
    editableParty: EditableParty,
    partyType: PartyType,
    id: number
  ): MainPartyClaimant | MainPartyRespondent {
    if (partyType === PARTY_TYPES.CLAIMANT) {
      return {
        id,
        partyType,
        nameClaimant: editableParty.name,
        addressClaimant: editableParty.address,
        stateOrCountyClaimant: editableParty.stateOrCounty,
        countryClaimantCode: editableParty.countryCode,
        telephoneClaimant: editableParty.telephone,
        emailClaimant: editableParty.email,
        typeClaimant: editableParty.typeClaimant,
        managerComplainant: editableParty.managerComplainant,
        typeClaimantOther: editableParty.typeClaimantOther,
        genderClaimant: editableParty.genderClaimant
      } as MainPartyClaimant;
    } else if (partyType === PARTY_TYPES.RESPONDENT) {
      return {
        id,
        partyType,
        nameRespondent: editableParty.name,
        addressRespondent: editableParty.address,
        stateOrCountyRespondent: editableParty.stateOrCounty,
        countryRespondantCode: editableParty.countryCode,
        telephoneRespondent: editableParty.telephone,
        emailRespondent: editableParty.email,
        typeRespondent: editableParty.typeRespondent,
        managerRespondent: editableParty.managerRespondent,
        typeRespondentOther: editableParty.typeRespondentOther,
        genderRespondent: editableParty.genderRespondent
      } as MainPartyRespondent;
    }

    return {} as any;
  }

  // ===== MAPPING METHODS FOR ADDITIONAL PARTIES =====
private mapFromEditablePartyAdditional(
  editableParty: EditableParty,
  partyType: PartyType,
  id: number
): AdditionalParty {
  const baseParty: AdditionalParty = {
    id,
    partyType,
    typeOfParty: editableParty.typeOfParty,
    typeOfPartyOther: editableParty.typeOfPartyOther,
    gender: editableParty.gender
  };

  // Map to appropriate field names based on party type
  if (partyType === PARTY_TYPES.CLAIMANT) {
    return {
      ...baseParty,
      nameClaimant: editableParty.name,
      addressClaimant: editableParty.address,
      stateOrCountyClaimant: editableParty.stateOrCounty,
      countryClaimantCode: editableParty.countryCode,
      telephoneClaimant: editableParty.telephone,
      emailClaimant: editableParty.email,
      typeOfParty: editableParty.typeOfParty,
      typeOfPartyOther: editableParty.typeOfPartyOther,
      gender: editableParty.gender
    };
  } else if (partyType === PARTY_TYPES.RESPONDENT) {
    return {
      ...baseParty,
      nameRespondent: editableParty.name,
      addressRespondent: editableParty.address,
      stateOrCountyRespondent: editableParty.stateOrCounty,
      countryRespondantCode: editableParty.countryCode,
      telephoneRespondent: editableParty.telephone,
      emailRespondent: editableParty.email,
      typeOfParty: editableParty.typeOfParty,
      typeOfPartyOther: editableParty.typeOfPartyOther,
      gender: editableParty.gender
    };
  }

  return baseParty;
}

  // ===== MAPPING METHODS FOR REPRESENTATIVES =====
  private mapFromEditablePartyRepresentative(
    editableParty: EditableParty,
    partyType: PartyType,
    id: number
  ): ClaimantRepBy | RespondentRepBy {
    if (partyType === PARTY_TYPES.CLAIMANT_REP) {
      return {
        id,
        partyType,
        nameClaimantRepBy: editableParty.name,
        addressClaimantRepBy: editableParty.address,
        stateOrCountyClaimantRepBy: editableParty.stateOrCounty,
        countryClaimantRepByCode: editableParty.countryCode,
        telephoneClaimantRepBy: editableParty.telephone,
        emailClaimantRepBy: editableParty.email
      } as ClaimantRepBy;
    } else if (partyType === PARTY_TYPES.RESPONDENT_REP) {
      return {
        id,
        partyType,
        nameRespondentRepBy: editableParty.name,
        addressRespondentRepBy: editableParty.address,
        stateOrCountyRespondentRepBy: editableParty.stateOrCounty,
        countryRespondantRepByCode: editableParty.countryCode,
        telephoneRespondentRepBy: editableParty.telephone,
        emailRespondentRepBy: editableParty.email
      } as RespondentRepBy;
    }

    return {} as any;
  }

  // ===== EMAIL VALIDATION =====
  onEmailValid(): void {
    const emailValue = this.editedPartyData.email;
    this.invalidEmailsValidation = emailValue ? !EMAIL_PATTERN.test(emailValue) : false;
  }

  onEmailValidNew(): void {
    const emailValue = this.newPartyData.email;
    this.invalidEmailsValidation = emailValue ? !EMAIL_PATTERN.test(emailValue) : false;
  }

  // ===== CHARACTER COUNT VALIDATION =====
  countCharsVal(fieldName: string): void {
    this.ValidationName = false;

    const fieldValidators = {
      name: () => this.validName = this.utilsService.countChars(this.editedPartyData?.name),
      address: () => this.validAddress = this.utilsService.countChars(this.editedPartyData?.address),
      stateOrCounty: () => this.validStateOrCounty = this.utilsService.countChars(this.editedPartyData?.stateOrCounty),
      prefix: () => this.validPrefix = this.utilsService.countChars(this.editedPartyData?.prefix),
      telephone: () => {
        if (!this.dispCountry) {
          this.validTel = this.utilsService.countChars(this.editedPartyData?.telephone);
        }
      },
      email: () => this.validEmail = this.utilsService.countChars(this.editedPartyData?.email)
    };

    const validator = fieldValidators[fieldName];
    if (validator) validator();
  }

  // Real-time character count validation for new party forms
  countCharsValNew(fieldName: string): void {
    this.ValidationName = false;

    const fieldValidators = {
      name: () => this.validName = this.utilsService.countChars(this.newPartyData?.name),
      address: () => this.validAddress = this.utilsService.countChars(this.newPartyData?.address),
      stateOrCounty: () => this.validStateOrCounty = this.utilsService.countChars(this.newPartyData?.stateOrCounty),
      prefix: () => this.validPrefix = this.utilsService.countChars(this.newPartyData?.prefix),
      telephone: () => this.validTel = this.utilsService.countChars(this.newPartyData?.telephone),
      email: () => this.validEmail = this.utilsService.countChars(this.newPartyData?.email)
    };

    const validator = fieldValidators[fieldName];
    if (validator) validator();
  }

  validate(): boolean {
    return !this.validCharActiveFlagValidation && !this.invalidEmailsValidation;
  }



  // REPLACE the existing resetValidation method with:
  resetValidation(): void {
    this.invalidEmailsValidation = false;
    this.validCharActiveFlagValidation = false;
    this.validName = false;
    this.validAddress = false;
    this.validTel = false;
    this.validEmail = false;
    this.ValidationName = false;
    this.validChar = [];

    // Reset dynamic field validations
    this.validTypeClaimantOther = true;
    this.validTypeRespondentOther = true;
    this.validTypeOfPartyOther = true;  // Add this line
    this.validmanagerComplainant = true;
    this.validmanagerRespondent = true;

    // Reset field visibility flags
    this.displaytypeClaimantOtherField = false;
    this.displaytypeRespondentOtherField = false;
    this.displayTypeOfPartyOtherField = false;  // Add this line
    this.managerClaimantField = false;
    this.managerRespondentField = false;
  }

  // ===== COUNTRY HANDLING =====
  async updateAllCountries(): Promise<void> {
    await Promise.all([
      this.getCountryByCode(this.claimants, this.getCountryCodeField(PARTY_TYPES.CLAIMANT), this.getCountryNameField(PARTY_TYPES.CLAIMANT)),
      this.getCountryByCode(this.claimantRepBys, 'countryClaimantRepByCode', 'countryClaimantRepBy'),
      this.getCountryByCode(this.respondents, this.getCountryCodeField(PARTY_TYPES.RESPONDENT), this.getCountryNameField(PARTY_TYPES.RESPONDENT)),
      this.getCountryByCode(this.respondentRepBys, 'countryRespondantRepByCode', 'countryRespondentRepBy')
    ]);
  }

  private getCountryCodeField(partyType: PartyType): string {
    return partyType === PARTY_TYPES.CLAIMANT ? 'countryClaimantCode' : 'countryRespondantCode';
  }

  private getCountryNameField(partyType: PartyType): string {
    return partyType === PARTY_TYPES.CLAIMANT ? 'countryClaimant' : 'countryRespondent';
  }

  async getCountryByCode(parties: any[], codeField: string, nameField: string): Promise<void> {
    const promises = parties
      .filter(party => party[codeField])
      .map(async party => {
        party[nameField] = await this.utilsService.getCountryByCode(party[codeField]);
      });

    await Promise.all(promises);
  }

  // ===== OPTION LABEL HELPERS =====
  getOptionLabel(options: any[], value: any): string {
    if (!value || !options?.length) return '';

    const option = options.find(opt =>
      String(opt.value) === String(value) || opt.value === Number(value)
    );

    return option?.label || '';
  }

  getTypeOfPartyLabel(value: any): string {
    return this.getOptionLabel(this.typeOfPartyOptions, value);
  }

  getGenderLabel(value: any): string {
    return this.getOptionLabel(this.genderOptions, value);
  }

  getPartyTypeLabel(partyType: PartyType): string {
    return PARTY_TYPE_LABELS[partyType] || '';
  }

  // ===== GET ALL PARTIES DATA =====
  getAllPartiesData(): AllParties {
    return {
      claimants: this.claimants,
      claimantRepBys: this.claimantRepBys,
      respondents: this.respondents,
      respondentRepBys: this.respondentRepBys
    };
  }

  // ===== GET SUMMARY =====
  getPartiesSummary() {
    return {
      totalClaimants: this.claimants.length,
      totalClaimantReps: this.claimantRepBys.length,
      totalRespondents: this.respondents.length,
      totalRespondentReps: this.respondentRepBys.length,
      totalParties: this.claimants.length + this.claimantRepBys.length +
        this.respondents.length + this.respondentRepBys.length,
      data: this.getAllPartiesData()
    };
  }

  printSummary(): void {
    const summary = this.getPartiesSummary();
    console.table(summary);
  }

  // ===== SAVE ALL PARTIES =====
  saveAllParties(): void {
    const allPartiesData = this.getAllPartiesData();

    console.log('========== SAVING ALL PARTIES ==========');
    console.log('Total Claimants:', this.claimants.length);
    console.log('Total Claimant Representatives:', this.claimantRepBys.length);
    console.log('Total Respondents:', this.respondents.length);
    console.log('Total Respondent Representatives:', this.respondentRepBys.length);
    console.log('\n--- COMPLETE DATA ---');
    console.log(JSON.stringify(allPartiesData, null, 2));
    console.log('========================================');

    // Uncomment to enable API call
    // const apiUrl = 'YOUR_API_ENDPOINT_HERE';
    // this.http.post(apiUrl, allPartiesData).subscribe({
    //   next: (response) => {
    //     console.log('✅ API Response Success:', response);
    //   },
    //   error: (error) => {
    //     console.error('❌ API Error:', error);
    //   }
    // });
  }

  // ===== DELETE PROTECTION =====
  /**
   * Check if party at index can be deleted
   * First party (index 0) of claimant and respondent cannot be deleted
   */
  canDeleteParty(partyType: PartyType, index: number): boolean {
    // Only protect first party (index 0) for main parties
    if (index === 0) {
      return partyType !== PARTY_TYPES.CLAIMANT && partyType !== PARTY_TYPES.RESPONDENT;
    }
    return true;
  }

  // ===== DELETE PARTY =====
  deletePartyAtIndex(partyType: PartyType, index: number): void {
    // Prevent deletion of first claimant or respondent
    if (!this.canDeleteParty(partyType, index)) {
      console.warn(`Cannot delete first ${partyType}. At least one ${partyType} is required.`);
      return;
    }

    this.getPartiesArray(partyType).splice(index, 1);
    this.emitChanges();
    this.cdr.detectChanges();
  }

  // ===== REPRESENTATIVE LIMITS =====
  /**
   * Check if can add more representatives
   * Representatives are limited to 1 record each
   */
  canAddRepresentative(partyType: PartyType): boolean {
    if (partyType === PARTY_TYPES.CLAIMANT_REP) {
      return this.claimantRepBys.length < 1;
    }
    if (partyType === PARTY_TYPES.RESPONDENT_REP) {
      return this.respondentRepBys.length < 1;
    }
    return true; // Main parties can have multiple
  }

  // ===== OLD METHODS (DEPRECATED - Keep for backward compatibility) =====
  isEditingParty(partyType: PartyType): boolean {
    return this.currentEditingPartyType === partyType;
  }

  toggleEditParty(partyType: PartyType): void {
    if (this.currentEditingPartyType === partyType) {
      this.currentEditingPartyType = null;
      this.partyBackup = null;
    } else {
      this.currentEditingPartyType = partyType;
      this.createPartyBackup(partyType);
    }
  }

  private createPartyBackup(partyType: PartyType): void {
    const parties = this.getPartiesArray(partyType);
    if (parties.length > 0) {
      this.partyBackup = JSON.parse(JSON.stringify(parties[0]));
    }
  }

  cancelEditParty(partyType: PartyType): void {
    if (this.partyBackup) {
      const parties = this.getPartiesArray(partyType);
      if (parties.length > 0) {
        parties[0] = this.partyBackup;
      }
    }
    this.currentEditingPartyType = null;
    this.partyBackup = null;
    this.cdr.detectChanges();
  }

  async saveEditedParty(partyType: PartyType): Promise<void> {
    await this.updateAllCountries();
    this.emitChanges();
    this.currentEditingPartyType = null;
    this.partyBackup = null;
    this.cdr.detectChanges();
  }

  addParty(partyType: PartyType): void {
    this.isEditMode = false;
    this.currentPartyType = partyType;
    this.currentEditingId = 1;
    this.editedPartyData = {};
    this.resetValidation();
    this.toggleAddEdit();
  }

  async saveParty(): Promise<void> {
    this.ValidationName = true;
    if (!this.editedPartyData?.name) {
      return;
    }
    this.ValidationName = false;

    this.validCharActiveFlagValidation = false;
    for (const key in this.editedPartyData) {
      this.validChar[key] = this.utilsService.countChars(this.editedPartyData[key]);
      if (this.validChar[key]) {
        this.validCharActiveFlagValidation = true;
      }
    }

    if (this.invalidEmailsValidation || this.validCharActiveFlagValidation) {
      return;
    }

    const parties = this.getPartiesArray(this.currentPartyType);
    const isMainParty = parties.length === 0;
    let specificParty;

    if (isMainParty) {
      specificParty = this.mapFromEditablePartyMain(
        this.editedPartyData,
        this.currentPartyType,
        this.currentEditingId
      );
    } else if (this.isRepresentative(this.currentPartyType)) {
      specificParty = this.mapFromEditablePartyRepresentative(
        this.editedPartyData,
        this.currentPartyType,
        this.currentEditingId
      );
    } else {
      specificParty = this.mapFromEditablePartyAdditional(
        this.editedPartyData,
        this.currentPartyType,
        this.currentEditingId
      );
    }

    parties.length = 0;
    parties.push(specificParty);

    await this.updateAllCountries();
    this.emitChanges();
    this.toggleAddEdit();
    this.cdr.detectChanges();
  }

  deleteParty(party: any): void {
    const partyType = party.partyType || this.getPartyTypeFromObject(party);
    const parties = this.getPartiesArray(partyType);
    parties.length = 0;
    this.emitChanges();
    this.cdr.detectChanges();
  }

  toggleAddEdit(): void {
    this.editAdd = !this.editAdd;
    if (!this.editAdd) {
      this.resetValidation();
    }
  }

} // End of PartyInformationComponent class